import React, { useState, useCallback, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import "./equipment.scss";
import { useEquipmentList, useEquipmentTypes } from "hooks/useEquipment";
import ContentContainer from "templates/content-container/contentContainer";
// import { UserUtils } from "app/data/user/userUtils";
import { PAGE_IDS } from "app/data/common/pageIds";
// import { useSelector } from "react-redux";
// import { userSelector } from "slices/user/userSlice";
// import UserState from "slices/user/UserState";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import EquipmentViewDetails from "./equipmentView/equipmentView";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Tag from "ui-components/molecules/tag/tag";
import EquipmentAddEditDetails from "./equipmentView/equipmentAddEditDetails";
import { TagColor } from "app/data/common/tagColor";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { statusOptions } from "app/data/equipment/models";
import { Formik } from 'formik';
import { Popover } from "ui-components/popover/popover";

const columnHelper = createColumnHelper<any>();

const Equipment: React.FC<{}> = () => {
  // const userState: UserState = useSelector(userSelector);
  const { data: equipmentTypeOptions = [], isLoading: isTypeLoading } = useEquipmentTypes();
  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [equipmentNumber, setEquipmentNumber] = useState<number | null>(null);
  const [showCreateTeam, setShowCreateTeam] = useState<boolean>(false);


  const getOption = (options: any[], value: string | undefined, key: string = 'value') => {
    return options.find((option) => option[key] === value);
  };

  const columns = [
    columnHelper.accessor("unitNumber", {
      id: "unitNumber",
      header: () => (
        <>
          Unit Number<Popover
            values={
              <div className="popover-value">
                Unique ID assigned to each equipment unit.
              </div>
            }
          />
        </>
      ),
      size: 70,
    }),
    columnHelper.accessor("equipmentType", {
      id: "equipmentType",
      header: () => (
        <>
          Type<Popover
            values={
              <div className="popover-value">
                Category of equipment.
              </div>
            }
          />
        </>
      ),
      size: 40,
    }),
    columnHelper.accessor("assetDescriptor", {
      id: "assetDescriptor",
      header: () => (
        <>
          Asset Descriptor<Popover
            values={
              <div className="popover-value">
                Describes the asset configuration, like Sleeper, Straight Truck, or TADC (Tandem Axle).
              </div>
            }
          />
        </>
      ),
      size: 70,
    }),

    columnHelper.accessor("make", {
      id: "make",
      header: "Make",
      size: 10,
    }),

    columnHelper.accessor("model", {
      id: "model",
      header: "Model",
      size: 10,
    }),

    columnHelper.accessor("year", {
      id: "year",
      header: () => (
        <>
          Year<Popover
            values={
              <div className="popover-value">
                Model year of the equipment.
              </div>
            }
          />
        </>
      ),
      size: 10,
    }),

    columnHelper.accessor("dotInspectionDate", {
      id: "dotInspectionDate",
      header: () => (
        <>
          DOT Inspection Date<Popover
            values={
              <div className="popover-value">
                Next scheduled Department of Transportation inspection date.
              </div>
            }
          />
        </>
      ),
      // cell: (cellProps) => {
      //   const isValid = cellProps.getValue();

      //   return (
      //     <Tag
      //       mods={{
      //         color:
      //           isValid === "VALID"
      //             ? TagColor.GREEN
      //             : isValid === "EXPIRING_SOON"
      //               ? TagColor.ORANGE
      //               : isValid === "EXPIRED"
      //                 ? TagColor.RED
      //                 : TagColor.GREY,
      //         uppercase: true,
      //       }}
      //     >
      //       {isValid}
      //     </Tag>
      //   )
      // },
      size: 70,
    }),

    columnHelper.accessor("location", {
      id: "location",
      header: () => (
        <>
          Terminal<Popover
            values={
              <div className="popover-value">
                Terminal or hub where this equipment is currently assigned.
              </div>
            }
          />
        </>
      ),
      size: 10,
    }),

    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <>
          Status<Popover
            values={
              <div className="popover-value">
                Shows whether the equipment is Active, Under Maintenance, or Out of Service.
              </div>
            }
          />
        </>
      ),
      cell: (cellProps) => {
        let cellValue = cellProps.getValue().toUpperCase();
        return (
          < Tag mods={{
            color: cellValue === "ACTIVE" ? TagColor.GREEN : TagColor.GREY, uppercase: true
          }}>
            {cellValue}
          </Tag >
        )
      },
      size: 0,
    })
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useEquipmentList(
    equipmentSearch || undefined,
    filterType || undefined,
    filterStatus || undefined
  );


  useEffect(() => {
    const timer = setTimeout(() => {
      setEquipmentSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleScroll = useCallback(() => {
    const scrollContainer = document.querySelector(".xgs-equipment-master__orders");
    if (!scrollContainer) return;

    const bottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop <=
      scrollContainer.clientHeight + 10;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);


  const onSearchStart = useCallback(() => {
    setEquipmentSearch(searchInput.trim());
  }, [searchInput]);

  const onSearchClear = (resetForm: (nextValues?: any) => void) => () => {
    setSearchInput("");
    setFilterType("");
    setFilterStatus("");
    setEquipmentSearch("");
    resetForm({ values: { type: "", status: "" } });
  };


  return (
    <ContentContainer
      pageId={PAGE_IDS.EQUIPMENT}
      title="Equipment Master"
      mods={{ 'full-width': true }}
    >
      <div className="xgs-equipment-master">
        <div className="xgs-list__controls">
          <Formik
            initialValues={{ type: filterType, status: filterStatus }}
            enableReinitialize={true}
            onSubmit={onSearchStart}
          >
            {({ getFieldProps, resetForm }) => (
              <div className="xgs-list__controls__search">
                <LabeledTextInput
                  labelMode={LabelModes.column}
                  className="xgs-list__controls__search__input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onKeyDown={(e) => (e.key === "Enter") && !isLoading && setEquipmentSearch(searchInput.trim())}
                  type="text"
                  placeholder="Search by Unit Number, VIN, or Make / Model..."
                />

                <XGSFormSelect
                  name="type"
                  className="filter-select"
                  placeholder="All Type"
                  isDisabled={isTypeLoading}
                  options={equipmentTypeOptions}
                  value={getOption(equipmentTypeOptions, filterType) || ""}
                  onValueChange={(option) => {
                    const newValue = option && typeof option.value === 'string'
                      ? option.value
                      : "";
                    setFilterType(newValue);
                  }}
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }
                  }
                />

                <XGSFormSelect
                  name="status"
                  className="filter-select"
                  placeholder="All Status"
                  options={statusOptions}
                  value={getOption(statusOptions, filterStatus) || ""}
                  onValueChange={(option) => {
                    const newValue = (option && typeof option.value === 'string')
                      ? option.value
                      : "";
                    setFilterStatus(newValue);
                  }}
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }}
                />

                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.blue}
                  disabled={isLoading}
                  onClick={onSearchStart}>
                  Search
                </Button>

                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.gray}
                  onClick={onSearchClear(resetForm)}>
                  Clear Filters
                </Button>
              </div>
            )}
          </Formik>
          <div className="xgs-list__controls__actions">
            {/* {UserUtils.isXGSAdministrator(userState.profile) && (
              <>
                <Button
                  className="xgs-equipment-master__create-equipment-button"
                  theme={ButtonThemes.blue}
                  onClick={() => setShowCreateTeam(true)}
                >
                  + Add Equipment
                </Button>
              </>
            )} */}
          </div>
        </div>
        {error && (
          <XGSErrorMessage>{error}</XGSErrorMessage>
        )}
        {!error && (
          <div className="xgs-equipment-master__orders">
            <Table
              isLoading={isLoading}
              columns={columns}
              data={data?.allItems || []}
              noResultsText="There are no records available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={handleScroll}
              responsive={true}
              sorting={true}
              minTableHeight={420}
              cursorPointer={true}
              onRowClicked={(row: any) => setEquipmentNumber(row.id)}
              autoResetSelectedRows={true}
            />
          </div>
        )}
        <EquipmentAddEditDetails
          show={showCreateTeam}
          onClose={() => setShowCreateTeam(false)}
          onTeamCreated={() => setEquipmentSearch("")}
        />
        <EquipmentViewDetails
          equipmentNumber={equipmentNumber}
          show={!!equipmentNumber}
          onClose={() => setEquipmentNumber(null)}
        />
      </div>
    </ContentContainer>
  );
};

export default Equipment;
